import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

// Material-UI components
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Divider,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    Grid,
    InputLabel,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Paper,
    Radio,
    RadioGroup,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

// Import Material Icons
import PersonIcon from '@mui/icons-material/Person';
import InfoIcon from '@mui/icons-material/Info';
import HandshakeIcon from '@mui/icons-material/Handshake';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';


// services
import StakeholderService from "../services/StakeholderService";
import TableService from "../services/TableService";

// components
import Output from "./OutputSH";
import Donations from "./DonationsSH";
import Actions from "./Actions";

// --- Funzioni di utilità per Codice Fiscale ---

const codiceFiscaleMeseMap = {
    'A': 1, 'B': 2, 'C': 3, 'D': 4, 'E': 5, 'H': 6,
    'L': 7, 'M': 8, 'P': 9, 'R': 10, 'S': 11, 'T': 12
};
const meseCodiceFiscaleMap = {
    1: 'A', 2: 'B', 3: 'C', 4: 'D', 5: 'E', 6: 'H',
    7: 'L', 8: 'M', 9: 'P', 10: 'R', 11: 'S', 12: 'T'
};

function formatDate(date) {
    if (!date) return null;
    var d = new Date(date),
        month = '' + (d.getMonth() + 1),
        day = '' + d.getDate(),
        year = d.getFullYear();

    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;

    return [year, month, day].join('-');
}

const inferFromCodiceFiscale = (cf) => {
    const result = { data_nascita: null, luogo_nascita: null, sesso: null };
    if (!cf || cf.length !== 16) return result;

    cf = cf.toUpperCase();

    let giorno = parseInt(cf.substring(9, 11), 10);
    if (giorno > 40) {
        result.sesso = 'F';
        giorno -= 40;
    } else {
        result.sesso = 'M';
    }

    const meseChar = cf.charAt(8);
    const mese = codiceFiscaleMeseMap[meseChar];

    let anno = parseInt(cf.substring(6, 8), 10);
    const currentYearLastTwoDigits = new Date().getFullYear() % 100;
    if (anno > currentYearLastTwoDigits) {
        anno = 1900 + anno;
    } else {
        anno = 2000 + anno;
    }

    if (giorno && mese && anno) {
        const dateObj = dayjs(`${anno}-${mese}-${giorno}`);
        if (dateObj.isValid()) {
            result.data_nascita = dateObj.format('YYYY-MM-DD');
        }
    }
    return result;
};


// --- Componente Stakeholder ---
const Stakeholder = (props) => {
    const { id } = useParams();
    let navigate = useNavigate();
    const sede = props.user?.sede || '';
    const user = props.user;
    const isAdmin = props.user?.roles?.includes("ROLE_ADMIN") || false;

    const initialStakeholderState = {
        id: null,
        titolo: null,
        attacco_lettera: null,
        ragione_sociale: null,
        genere: '',
        nome: '',
        cognome: '',
        codice_fiscale: '',
        partita_iva: null,
        civico: null,
        indirizzo: null,
        cap: null,
        citta: null,
        provincia: null,
        telefono: '',
        cellulare: '',
        email: '',
        note: null,
        data_nascita: null,
        luogo_nascita: null,
        sesso: null,
        fonte: null,
        data_inserimento: formatDate(new Date()),
        comunicazioni_email: true,
        comunicazioni_cartacee: true,
        consenso_invio_materiale_informativo: true,
        consenso_ringraziamaneti: true,
        consenso_SMS: true,
        consenso_contatto_telefonico: true,
        donatore: null,
        categoria: [],
        tipo_donatore: null,
        stato: null,
        data_decesso: null,
        tessera: null,
        pec: null,
        codice_sdi: null,
        donazioni_anno_corrente: null,
        totale_donazioni: null,
        n_donazioni: null,
        termometro_dimensione: null,
        termometro_fedelta: null,
        termometro_temperatura: null,
        data_prima_donazione: null,
        importo_prima_donazione: null,
        mod_prima_donazione: null,
        data_ultima_donazione: null,
        importo_ultima_donazione: null,
        mesi_da_ultima_donazione: null,
        utente_ultima_modifica: null,
        gdpr_2016_979_consenso_il: formatDate(new Date()),
        gdpr_2016_979_negato_il: null,
        gdpr_2016_979_consenso: true,
        dlgs_196_2003_consenso_il: formatDate(new Date()),
        dlgs_196_2003_negato_il: null,
        dlgs_196_2003_consenso: true,
        tipologia: null,
        titolo_studio: null,
        professione: null,
        stato_civile: null,
        figli: null,
        corso_faf: null,
        testo_corso_faf: null,
        corso_vol: null,
        testo_corso_vol: null,
        data_recesso: null,
        data_socio: null,
        tipologia_contratto: null,
        data_assunzione: null,
        data_colloquio_vol_faf: null,
        data_colloquio_socio: null,
        data_ospite: null,
        invalidita: null,
        perc_invalidita: null,
        riconoscimento_handicap: null,
        iscrizione_liste_collocamento: null,
        pensione: null,
        data_primo_contatto_vol: null,
        tipo_vol: [],
        disponibilita: null,
        servizio_riferimento_curante: '',
        servizio_riferimento_tel: '',
        servizio_riferimento_mail: '',
        servizio_riferimento_indirizzo: '',
        contatto_emergenza_nome: '',
        contatto_emergenza_tel: '',
        contatto_emergenza_mail: '',
        contatto_familiare_nome: '',
        contatto_familiare_tel: '',
        contatto_familiare_mail: '',
        legge_104: false,
        legge_68_99: false,
        pensione_invalidita: false,
        conoscenza_club_itaca: '',
        sede: sede,
        codice: ""
    };

    const [currentStakeholder, setCurrentStakeholder] = useState(initialStakeholderState);
    const [selectedMenu, setSelectedMenu] = useState(props.panel === 4 ? 'azioni' : 'anagrafica');
    const [errors, setErrors] = useState({});

    const getStakeholder = id => {
        StakeholderService.get(id)
            .then(response => {
                setCurrentStakeholder({
                    ...response.data,
                    categoria: (response.data.categoria) ? response.data.categoria.split(",").map((item) => item.trim()) : [],
                    tipo_vol: (response.data.tipo_vol) ? response.data.tipo_vol.split(",").map((item) => item.trim()) : [],
                    data_nascita: (response.data.data_nascita) ? formatDate(response.data.data_nascita) : null,
                    data_inserimento: (response.data.data_inserimento) ? formatDate(response.data.data_inserimento) : null,
                    data_decesso: (response.data.data_decesso) ? formatDate(response.data.data_decesso) : null,
                    data_prima_donazione: (response.data.data_prima_donazione) ? formatDate(response.data.data_prima_donazione) : null,
                    data_ultima_donazione: (response.data.data_ultima_donazione) ? formatDate(response.data.data_ultima_donazione) : null,
                    gdpr_2016_979_consenso_il: (response.data.gdpr_2016_979_consenso_il) ? formatDate(response.data.gdpr_2016_979_consenso_il) : null,
                    dlgs_196_2003_consenso_il: (response.data.dlgs_196_2003_consenso_il) ? formatDate(response.data.dlgs_196_2003_consenso_il) : null,
                    data_recesso: (response.data.data_recesso) ? formatDate(response.data.data_recesso) : null,
                    data_socio: (response.data.data_socio) ? formatDate(response.data.data_socio) : null,
                    data_assunzione: (response.data.data_assunzione) ? formatDate(response.data.data_assunzione) : null,
                    data_colloquio_vol_faf: (response.data.data_colloquio_vol_faf) ? formatDate(response.data.data_colloquio_vol_faf) : null,
                    data_colloquio_socio: (response.data.data_colloquio_socio) ? formatDate(response.data.data_colloquio_socio) : null,
                    data_ospite: (response.data.data_ospite) ? formatDate(response.data.data_ospite) : null,
                    data_primo_contatto_vol: (response.data.data_primo_contatto_vol) ? formatDate(response.data.data_primo_contatto_vol) : null,
                    comunicazioni_email: response.data.comunicazioni_email === true || response.data.comunicazioni_email === 'true',
                    comunicazioni_cartacee: response.data.comunicazioni_cartacee === true || response.data.comunicazioni_cartacee === 'true',
                    consenso_invio_materiale_informativo: response.data.consenso_invio_materiale_informativo === true || response.data.consenso_invio_materiale_informativo === 'true',
                    consenso_ringraziamaneti: response.data.consenso_ringraziamaneti === true || response.data.consenso_ringraziamaneti === 'true',
                    consenso_SMS: response.data.consenso_SMS === true || response.data.consenso_SMS === 'true',
                    consenso_contatto_telefonico: response.data.consenso_contatto_telefonico === true || response.data.consenso_contatto_telefonico === 'true',
                    gdpr_2016_979_consenso: response.data.gdpr_2016_979_consenso === true || response.data.gdpr_2016_979_consenso === 'true',
                    dlgs_196_2003_consenso: response.data.dlgs_196_2003_consenso === true || response.data.dlgs_196_2003_consenso === 'true',
                    corso_faf: response.data.corso_faf === true || response.data.corso_faf === 'true',
                    corso_vol: response.data.corso_vol === true || response.data.corso_vol === 'true',
                    invalidita: response.data.invalidita === true || response.data.invalidita === 'true',
                    iscrizione_liste_collocamento: response.data.iscrizione_liste_collocamento === true || response.data.iscrizione_liste_collocamento === 'true',
                    pensione: response.data.pensione === true || response.data.pensione === 'true',
                });
            })
            .catch(e => {
                console.log(e);
            });
    };

    useEffect(() => {
        if (id) {
            getStakeholder(id);
        }
        retrieveTables();
    }, [id]);

    const validateForm = () => {
        let tempErrors = {};
        let isValid = true;

        if (!currentStakeholder.nome && !currentStakeholder.ragione_sociale) {
            tempErrors.nome = "Nome o Ragione Sociale richiesti.";
            tempErrors.ragione_sociale = "Nome o Ragione Sociale richiesti.";
            isValid = false;
        }
        if (currentStakeholder.nome && !currentStakeholder.cognome) {
            tempErrors.cognome = "Cognome richiesto se il nome è compilato.";
            isValid = false;
        }
        if (currentStakeholder.email && !/\S+@\S+\.\S+/.test(currentStakeholder.email)) {
            tempErrors.email = "L'email non è valida.";
            isValid = false;
        }
        if (currentStakeholder.telefono && !/^\+?[0-9\s.-]{7,25}$/.test(currentStakeholder.telefono)) {
            tempErrors.telefono = "Formato telefono non valido.";
            isValid = false;
        }
        if (currentStakeholder.cellulare && !/^\+?[0-9\s.-]{7,25}$/.test(currentStakeholder.cellulare)) {
            tempErrors.cellulare = "Formato cellulare non valido.";
            isValid = false;
        }
        const cfRegex = /^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/i;
        if (currentStakeholder.codice_fiscale && !cfRegex.test(currentStakeholder.codice_fiscale)) {
            tempErrors.codice_fiscale = "Codice Fiscale non valido (es. ABCDEF12A34B567C).";
            isValid = false;
        }
        if (!currentStakeholder.data_inserimento) {
            tempErrors.data_inserimento = "La data di inserimento è richiesta.";
            isValid = false;
        }

        setErrors(tempErrors);
        return isValid;
    };

    const saveStakeholder = () => {
        if (!validateForm()) {
            alert("Per favore, correggi gli errori nel form prima di salvare.");
            return;
        }

        StakeholderService.create({
            ...currentStakeholder,
            categoria: currentStakeholder.categoria?.join(', ') || null,
            tipo_vol: currentStakeholder.tipo_vol?.join(', ') || null
        })
            .then(response => {
                // ... (codice di gestione della risposta, non è necessario modificarlo)
                alert("Contatto salvato correttamente!");
                navigate(`/dashboard/stakeholders/${response.data.id}`);
            })
            .catch(e => {
                console.log(e);
                alert("Errore durante il salvataggio del contatto.");
            });
    };

    const handleInputChange = (event) => {
        const { name, value, type, checked } = event.target;
        const newStakeholder = {
            ...currentStakeholder,
            [name]: type === 'checkbox' || type === 'radio' ? checked : value
        };

        if (name === "codice_fiscale" && value.length === 16) {
            const inferred = inferFromCodiceFiscale(value);
            newStakeholder.data_nascita = inferred.data_nascita || newStakeholder.data_nascita;
            newStakeholder.sesso = inferred.sesso || newStakeholder.sesso;
        }

        setCurrentStakeholder(newStakeholder);

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const [tables, setTables] = useState([]);

    const retrieveTables = () => {
        TableService.getAll()
            .then(response => {
                setTables(response.data);
            })
            .catch(e => {
                console.log(e);
            });
    };

    const theme = useTheme();

    function getStyles(name, selectedItems, theme) {
        return {
            fontWeight: selectedItems?.indexOf(name) === -1 ?
                theme.typography.fontWeightRegular :
                theme.typography.fontWeightMedium,
        };
    }

    const handleCategoryChange = (event) => {
        const { target: { value } } = event;
        const newCategories = typeof value === 'string' ? value.split(',') : value;
        setCurrentStakeholder({ ...currentStakeholder, categoria: newCategories });
    };

    const handleTipVolChange = (event) => {
        const { target: { value } } = event;
        const newTipoVol = typeof value === 'string' ? value.split(',') : value;
        setCurrentStakeholder({ ...currentStakeholder, tipo_vol: newTipoVol });
    };

    const updateStakeholder = () => {
        if (!validateForm()) {
            alert("Per favore, correggi gli errori nel form prima di aggiornare.");
            return;
        }

        const dataToUpdate = { ...currentStakeholder };
        delete dataToUpdate.id;
        delete dataToUpdate.createdAt;
        delete dataToUpdate.updatedAt;
        delete dataToUpdate.codice;

        dataToUpdate.categoria = dataToUpdate.categoria?.join(', ') || null;
        dataToUpdate.tipo_vol = dataToUpdate.tipo_vol?.join(', ') || null;

        StakeholderService.update(currentStakeholder.id, dataToUpdate)
            .then(response => {
                alert("Contatto aggiornato correttamente");
            })
            .catch(e => {
                console.log(e);
                alert("Errore durante l'aggiornamento del contatto.");
            });
    };

    const isVolontario = currentStakeholder.categoria?.includes('Volontario');
    const isSocio = currentStakeholder.categoria?.includes('Socio Club');

    const deletestakeholder = () => {
        if (window.confirm("Sei sicuro di voler eliminare questo stakeholder?")) {
            StakeholderService.remove(currentStakeholder.id)
                .then(response => {
                    navigate("/stakeholders");
                })
                .catch(e => {
                    console.log(e);
                    alert("Errore durante l'eliminazione del contatto.");
                });
        }
    };

    const renderFormSection = () => {
        switch (selectedMenu) {
            case 'anagrafica':
                return (
                    <Box sx={{ p: 3 }}>
                        <Typography variant="h4" gutterBottom sx={{ textTransform: 'uppercase' }}>Dati Anagrafici</Typography>
                        <Divider sx={{ mb: 3 }} />
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField id="data_inserimento" label="Primo contatto" name="data_inserimento" type="date" InputLabelProps={{ shrink: true }} variant="outlined" fullWidth value={currentStakeholder.data_inserimento || ''} onChange={handleInputChange} error={!!errors.data_inserimento} helperText={errors.data_inserimento} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <FormControl fullWidth>
                                    <InputLabel id="categoria-label">Categoria</InputLabel>
                                    <Select labelId="categoria-label" id="categoria" multiple value={currentStakeholder.categoria || []} onChange={handleCategoryChange} input={<OutlinedInput id="select-multiple-chip" label="Categoria" />} renderValue={(selected) => (<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>{selected.map((value) => (<Chip key={value} label={value} />))}</Box>)}>
                                        {['Socio Club', 'Ospite', 'Candidato', 'Volontario', 'Non Iscritto', 'Espulso', 'Ritirato'].map((category) => (
                                            <MenuItem key={category} value={category} style={getStyles(category, currentStakeholder.categoria, theme)}>
                                                {category}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField id="codice" label="Codice" name="codice" disabled variant="outlined" fullWidth value={currentStakeholder.codice || ''} InputLabelProps={{ shrink: true }} onChange={handleInputChange} error={!!errors.codice} helperText={errors.codice} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField id="nome" label="Nome" name="nome" variant="outlined" fullWidth value={currentStakeholder.nome || ''} InputLabelProps={{ shrink: true }} onChange={handleInputChange} error={!!errors.nome} helperText={errors.nome} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField id="cognome" label="Cognome" name="cognome" variant="outlined" fullWidth value={currentStakeholder.cognome || ''} InputLabelProps={{ shrink: true }} onChange={handleInputChange} error={!!errors.cognome} helperText={errors.cognome} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <FormControl fullWidth>
                                    <InputLabel id="sesso-label">Sesso</InputLabel>
                                    <Select labelId="sesso-label" id="sesso" value={currentStakeholder.sesso || ''} name="sesso" onChange={handleInputChange} label="Sesso">
                                        <MenuItem value={"F"}>F</MenuItem>
                                        <MenuItem value={"M"}>M</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <FormControl fullWidth>
                                    <InputLabel id="genere-label">Genere</InputLabel>
                                    <Select labelId="genere-label" id="genere" value={currentStakeholder.genere || ''} name="genere" onChange={handleInputChange} label="Genere">
                                        <MenuItem value={"F"}>F</MenuItem>
                                        <MenuItem value={"M"}>M</MenuItem>
                                        <MenuItem value={"Altro"}>Altro</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField id="data_nascita" label="Data di nascita" variant="outlined" type="date" name="data_nascita" fullWidth InputLabelProps={{ shrink: true }} value={currentStakeholder.data_nascita || ''} onChange={handleInputChange} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField id="luogo_nascita" label="Luogo di nascita" variant="outlined" name="luogo_nascita" fullWidth InputLabelProps={{ shrink: true }} value={currentStakeholder.luogo_nascita || ''} onChange={handleInputChange} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField id="codice_fiscale" label="Codice fiscale" variant="outlined" name="codice_fiscale" fullWidth InputLabelProps={{ shrink: true }} value={currentStakeholder.codice_fiscale || ''} onChange={handleInputChange} error={!!errors.codice_fiscale} helperText={errors.codice_fiscale} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField id="telefono" label="Telefono" variant="outlined" name="telefono" fullWidth value={currentStakeholder.telefono || ''} InputLabelProps={{ shrink: true }} onChange={handleInputChange} error={!!errors.telefono} helperText={errors.telefono} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField id="cellulare" label="Cellulare" variant="outlined" name="cellulare" fullWidth value={currentStakeholder.cellulare || ''} InputLabelProps={{ shrink: true }} onChange={handleInputChange} error={!!errors.cellulare} helperText={errors.cellulare} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField id="email" label="Email" variant="outlined" name="email" fullWidth value={currentStakeholder.email || ''} InputLabelProps={{ shrink: true }} onChange={handleInputChange} error={!!errors.email} helperText={errors.email} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6}>
                                <TextField id="indirizzo" label="Indirizzo" variant="outlined" name="indirizzo" fullWidth value={currentStakeholder.indirizzo || ''} InputLabelProps={{ shrink: true }} onChange={handleInputChange} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <TextField id="civico" label="Civico" variant="outlined" name="civico" fullWidth value={currentStakeholder.civico || ''} InputLabelProps={{ shrink: true }} onChange={handleInputChange} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField id="citta" label="Città" variant="outlined" name="citta" fullWidth value={currentStakeholder.citta || ''} InputLabelProps={{ shrink: true }} onChange={handleInputChange} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField id="provincia" label="Provincia" variant="outlined" name="provincia" fullWidth value={currentStakeholder.provincia || ''} InputLabelProps={{ shrink: true }} onChange={handleInputChange} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField id="cap" label="CAP" variant="outlined" name="cap" fullWidth value={currentStakeholder.cap || ''} InputLabelProps={{ shrink: true }} onChange={handleInputChange} />
                            </Grid>
                        </Grid>
                    </Box>
                );
            case 'donazioni':
                return (
                    <Box sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>Donazioni</Typography>
                        <Divider sx={{ mb: 3 }} />
                        {(currentStakeholder.id) ? <Donations sh={currentStakeholder} /> : <Typography>Salva il contatto per visualizzare le donazioni.</Typography>}
                    </Box>
                );
            case 'informazioni_dettagliate': // NUOVO CASE UNIFICATO
                return (
                    <Box sx={{ p: 3 }}>
                        <Typography variant="h4" gutterBottom sx={{ textTransform: 'uppercase' }}>Informazioni Dettagliate</Typography>
                        <Divider sx={{ mb: 3 }} />

                        {/* --- SEZIONE SERVIZIO DI RIFERIMENTO --- */}
                        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Servizio di Riferimento</Typography>
                        <Divider sx={{ mb: 3 }} />
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <TextField name="servizio_riferimento_curante" label="Nome e Cognome Curante" fullWidth variant="outlined" value={currentStakeholder.servizio_riferimento_curante || ''} onChange={handleInputChange} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField name="servizio_riferimento_tel" label="Telefono Curante" fullWidth variant="outlined" value={currentStakeholder.servizio_riferimento_tel || ''} onChange={handleInputChange} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField name="servizio_riferimento_mail" label="Email Curante" type="email" fullWidth variant="outlined" value={currentStakeholder.servizio_riferimento_mail || ''} onChange={handleInputChange} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField name="servizio_riferimento_indirizzo" label="Indirizzo Servizio" fullWidth multiline rows={2} variant="outlined" value={currentStakeholder.servizio_riferimento_indirizzo || ''} onChange={handleInputChange} />
                            </Grid>
                        </Grid>

                        {/* --- SEZIONE CONTATTI AGGIUNTIVI --- */}
                        <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>Contatti Aggiuntivi</Typography>
                        <Divider sx={{ mb: 3 }} />
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField name="contatto_emergenza_nome" label="Contatto Emergenza (Nome)" fullWidth variant="outlined" value={currentStakeholder.contatto_emergenza_nome || ''} onChange={handleInputChange} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <TextField name="contatto_emergenza_tel" label="Telefono" fullWidth variant="outlined" value={currentStakeholder.contatto_emergenza_tel || ''} onChange={handleInputChange} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField name="contatto_familiare_nome" label="Contatto Familiare (Nome)" fullWidth variant="outlined" value={currentStakeholder.contatto_familiare_nome || ''} onChange={handleInputChange} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <TextField name="contatto_familiare_tel" label="Telefono" fullWidth variant="outlined" value={currentStakeholder.contatto_familiare_tel || ''} onChange={handleInputChange} />
                            </Grid>
                        </Grid>

                        {/* --- SEZIONE INVALIDITÀ E LEGGI --- */}
                        <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>Invalidità e Leggi</Typography>
                        <Divider sx={{ mb: 3 }} />
                        <Grid container spacing={3} alignItems="flex-start">
                            <Grid item xs={12} sm={4}>
                                <TextField name="perc_invalidita" label="Percentuale di Invalidità (%)" type="number" fullWidth variant="outlined" value={currentStakeholder.perc_invalidita || ''} onChange={handleInputChange} inputProps={{ min: 0, max: 100 }} />
                            </Grid>
                            <Grid item xs={12} sm={8}>
                                <FormGroup sx={{ mt: 1 }}>
                                    <FormControlLabel control={<Checkbox name="legge_104" checked={!!currentStakeholder.legge_104} onChange={handleInputChange} />} label="Legge 104" />
                                    <FormControlLabel control={<Checkbox name="legge_68_99" checked={!!currentStakeholder.legge_68_99} onChange={handleInputChange} />} label="Legge 68/99 (Collocamento mirato)" />
                                    <FormControlLabel control={<Checkbox name="pensione_invalidita" checked={!!currentStakeholder.pensione_invalidita} onChange={handleInputChange} />} label="Pensione di Invalidità" />
                                </FormGroup>
                            </Grid>
                        </Grid>

                        {/* --- SEZIONE SPECIFICA PER SOCIO CLUB (condizionale) --- */}
                        {isSocio && (
                            <>
                                <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>Dettagli Iscrizione Socio</Typography>
                                <Divider sx={{ mb: 3 }} />
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <TextField id="data_colloquio_socio" label="Data colloquio" InputLabelProps={{ shrink: true }} variant="outlined" type="date" name="data_colloquio_socio" fullWidth value={currentStakeholder.data_colloquio_socio || ''} onChange={handleInputChange} />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <TextField id="data_ospite" label="Data ospite" InputLabelProps={{ shrink: true }} variant="outlined" type="date" name="data_ospite" fullWidth value={currentStakeholder.data_ospite || ''} onChange={handleInputChange} />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <TextField id="data_socio" label="Data socio" InputLabelProps={{ shrink: true }} variant="outlined" type="date" name="data_socio" fullWidth value={currentStakeholder.data_socio || ''} onChange={handleInputChange} />
                                    </Grid>
                                </Grid>
                            </>
                        )}

                        {/* --- SEZIONE ALTRE INFORMAZIONI --- */}
                        <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>Altre Informazioni</Typography>
                        <Divider sx={{ mb: 3 }} />
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <TextField name="conoscenza_club_itaca" label="Come sei venuto a conoscenza di Club Itaca?" fullWidth multiline rows={4} variant="outlined" value={currentStakeholder.conoscenza_club_itaca || ''} onChange={handleInputChange} />
                            </Grid>
                        </Grid>
                    </Box>
                );
            case 'volontario_familiare':
                return (
                    <Box sx={{ p: 3 }}>
                        <Typography variant="h4" gutterBottom sx={{ textTransform: 'uppercase' }}>Volontario/Familiare</Typography>
                        <Divider sx={{ mb: 3 }} />
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField id="data_primo_contatto_vol" label="Data primo contatto" variant="outlined" type="date" InputLabelProps={{ shrink: true }} name="data_primo_contatto_vol" fullWidth value={currentStakeholder.data_primo_contatto_vol || ''} onChange={handleInputChange} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField id="data_colloquio_vol_faf" label="Data colloquio" variant="outlined" type="date" InputLabelProps={{ shrink: true }} name="data_colloquio_vol_faf" fullWidth value={currentStakeholder.data_colloquio_vol_faf || ''} onChange={handleInputChange} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <FormControl fullWidth>
                                    <InputLabel id="tipo-volontario-label">Tipologia volontario</InputLabel>
                                    <Select labelId="tipo-volontario-label" id="tipo_vol" multiple value={currentStakeholder.tipo_vol || []} onChange={handleTipVolChange} input={<OutlinedInput id="select-multiple-chip-vol" label="Tipologia volontario" />} renderValue={(selected) => (<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>{selected.map((value) => (<Chip key={value} label={value} />))}</Box>)}>
                                        {tables.map((cat) =>
                                            (cat.riferimento === 10) && (
                                                <MenuItem key={cat.id} value={cat.campi} style={getStyles(cat.campi, currentStakeholder.tipo_vol, theme)}>
                                                    {cat.campi}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField id="disponibilita" label="Disponibilità" variant="outlined" InputLabelProps={{ shrink: true }} name="disponibilita" fullWidth value={currentStakeholder.disponibilita || ''} onChange={handleInputChange} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <FormControl component="fieldset" margin="normal">
                                    <FormLabel component="legend">Corso volontari</FormLabel>
                                    <RadioGroup row name="corso_vol" value={currentStakeholder.corso_vol} onChange={handleInputChange}>
                                        <FormControlLabel value={true} control={<Radio />} label="Si" />
                                        <FormControlLabel value={false} control={<Radio />} label="No" />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField id="testo_corso_vol" InputLabelProps={{ shrink: true }} label="Info corso volontari" variant="outlined" fullWidth multiline rows={4} name="testo_corso_vol" value={currentStakeholder.testo_corso_vol || ''} onChange={handleInputChange} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <FormControl component="fieldset" margin="normal">
                                    <FormLabel component="legend">Corso FAF</FormLabel>
                                    <RadioGroup row name="corso_faf" value={currentStakeholder.corso_faf} onChange={handleInputChange}>
                                        <FormControlLabel value={true} control={<Radio />} label="Si" />
                                        <FormControlLabel value={false} control={<Radio />} label="No" />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField id="testo_corso_faf" label="Info corso FAF" InputLabelProps={{ shrink: true }} variant="outlined" fullWidth multiline rows={4} name="testo_corso_faf" value={currentStakeholder.testo_corso_faf || ''} onChange={handleInputChange} />
                            </Grid>
                        </Grid>
                    </Box>
                );
            case 'azioni':
                return (
                    <Box sx={{ p: 3 }}>
                        <Typography variant="h4" gutterBottom sx={{ textTransform: 'uppercase' }}>Azioni</Typography>
                        <Divider sx={{ mb: 3 }} />
                        {(currentStakeholder.id) ? <Actions stakeholder={currentStakeholder} user={user} /> : <Typography>Salva il contatto per aggiungere azioni.</Typography>}
                    </Box>
                );
            case 'uscite':
                return (
                    <Box sx={{ p: 3 }}>
                        <Typography variant="h4" gutterBottom sx={{ textTransform: 'uppercase' }}>Uscite</Typography>
                        <Divider sx={{ mb: 3 }} />
                        {(currentStakeholder.id) ? <Output sh={currentStakeholder} /> : <Typography>Salva il contatto per visualizzare le uscite.</Typography>}
                    </Box>
                );
            case 'consensi':
                return (
                    <Box sx={{ p: 3 }}>
                        <Typography variant="h4" gutterBottom sx={{ textTransform: 'uppercase' }}>Consensi</Typography>
                        <Divider sx={{ mb: 3 }} />
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <FormControl component="fieldset" margin="normal">
                                    <FormLabel component="legend">Comunicazioni email</FormLabel>
                                    <RadioGroup row name="comunicazioni_email" value={currentStakeholder.comunicazioni_email} onChange={handleInputChange}>
                                        <FormControlLabel value={true} control={<Radio />} label="Si" />
                                        <FormControlLabel value={false} control={<Radio />} label="No" />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl component="fieldset" margin="normal">
                                    <FormLabel component="legend">Comunicazioni cartacee</FormLabel>
                                    <RadioGroup row name="comunicazioni_cartacee" value={currentStakeholder.comunicazioni_cartacee} onChange={handleInputChange}>
                                        <FormControlLabel value={true} control={<Radio />} label="Si" />
                                        <FormControlLabel value={false} control={<Radio />} label="No" />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl component="fieldset" margin="normal">
                                    <FormLabel component="legend">Invio materiale informativo</FormLabel>
                                    <RadioGroup row name="consenso_invio_materiale_informativo" value={currentStakeholder.consenso_invio_materiale_informativo} onChange={handleInputChange}>
                                        <FormControlLabel value={true} control={<Radio />} label="Si" />
                                        <FormControlLabel value={false} control={<Radio />} label="No" />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl component="fieldset" margin="normal">
                                    <FormLabel component="legend">Ringraziamenti</FormLabel>
                                    <RadioGroup row name="consenso_ringraziamaneti" value={currentStakeholder.consenso_ringraziamaneti} onChange={handleInputChange}>
                                        <FormControlLabel value={true} control={<Radio />} label="Si" />
                                        <FormControlLabel value={false} control={<Radio />} label="No" />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl component="fieldset" margin="normal">
                                    <FormLabel component="legend">Invio SMS</FormLabel>
                                    <RadioGroup row name="consenso_SMS" value={currentStakeholder.consenso_SMS} onChange={handleInputChange}>
                                        <FormControlLabel value={true} control={<Radio />} label="Si" />
                                        <FormControlLabel value={false} control={<Radio />} label="No" />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl component="fieldset" margin="normal">
                                    <FormLabel component="legend">Contatto telefonico</FormLabel>
                                    <RadioGroup row name="consenso_contatto_telefonico" value={currentStakeholder.consenso_contatto_telefonico} onChange={handleInputChange}>
                                        <FormControlLabel value={true} control={<Radio />} label="Si" />
                                        <FormControlLabel value={false} control={<Radio />} label="No" />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl component="fieldset" margin="normal">
                                    <FormLabel component="legend">GDPR 2016/679</FormLabel>
                                    <RadioGroup row name="gdpr_2016_979_consenso" value={currentStakeholder.gdpr_2016_979_consenso} onChange={handleInputChange}>
                                        <FormControlLabel value={true} control={<Radio />} label="Si" />
                                        <FormControlLabel value={false} control={<Radio />} label="No" />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField id="gdpr_2016_979_consenso_il" label="in data" variant="outlined" type="date" name="gdpr_2016_979_consenso_il" fullWidth InputLabelProps={{ shrink: true }} value={currentStakeholder.gdpr_2016_979_consenso_il || ''} onChange={handleInputChange} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl component="fieldset" margin="normal">
                                    <FormLabel component="legend">DLGS 196/2003</FormLabel>
                                    <RadioGroup row name="dlgs_196_2003_consenso" value={currentStakeholder.dlgs_196_2003_consenso} onChange={handleInputChange}>
                                        <FormControlLabel value={true} control={<Radio />} label="Si" />
                                        <FormControlLabel value={false} control={<Radio />} label="No" />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField id="dlgs_196_2003_consenso_il" label="in data" variant="outlined" type="date" name="dlgs_196_2003_consenso_il" fullWidth InputLabelProps={{ shrink: true }} value={currentStakeholder.dlgs_196_2003_consenso_il || ''} onChange={handleInputChange} />
                            </Grid>
                        </Grid>
                    </Box>
                );
            default:
                return null;
        }
    };

    return (
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, p: 2 }}>
            {/* Left Menu */}
            <Paper elevation={3} sx={{
                width: { xs: '100%', md: '250px' },
                mb: { xs: 2, md: 0 },
                mr: { xs: 0, md: 3 },
                p: 1,
                flexShrink: 0,
            }}>
                <Typography variant="h6" sx={{ p: 2, backgroundColor: "#1976d2", color: "white", borderRadius: '4px' }}>
                    {currentStakeholder.cognome ? `${currentStakeholder.nome} ${currentStakeholder.cognome}` : currentStakeholder.ragione_sociale || 'Nuovo Contatto'}
                </Typography>
                <Divider sx={{ my: 1 }} />
                <List component="nav">
                    <ListItem button selected={selectedMenu === 'anagrafica'} onClick={() => setSelectedMenu('anagrafica')}>
                        <ListItemIcon><PersonIcon /></ListItemIcon>
                        <ListItemText primary="Anagrafica" />
                    </ListItem>

                    {!isVolontario && (
                        <ListItem button selected={selectedMenu === 'informazioni_dettagliate'} onClick={() => setSelectedMenu('informazioni_dettagliate')}>
                            <ListItemIcon><InfoIcon /></ListItemIcon>
                            <ListItemText primary="Informazioni Dettagliate" />
                        </ListItem>
                    )}

                    {isVolontario && (
                        <ListItem button selected={selectedMenu === 'volontario_familiare'} onClick={() => setSelectedMenu('volontario_familiare')}>
                            <ListItemIcon><HandshakeIcon /></ListItemIcon>
                            <ListItemText primary="Volontario/Familiare" />
                        </ListItem>
                    )}

                    <ListItem button selected={selectedMenu === 'azioni'} onClick={() => setSelectedMenu('azioni')}>
                        <ListItemIcon><RocketLaunchIcon /></ListItemIcon>
                        <ListItemText primary="Azioni" />
                    </ListItem>

                    <ListItem button selected={selectedMenu === 'consensi'} onClick={() => setSelectedMenu('consensi')}>
                        <ListItemIcon><CheckCircleOutlineIcon /></ListItemIcon>
                        <ListItemText primary="Consensi" />
                    </ListItem>
                </List>
            </Paper>

            {/* Main Content Area */}
            <Paper elevation={3} sx={{ flexGrow: 1, minWidth: 0, p: { xs: 2, sm: 3 } }}>
                {renderFormSection()}
                <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <Button
                        variant="contained"
                        onClick={(currentStakeholder.id) ? updateStakeholder : saveStakeholder}
                        disabled={Object.values(errors).some(error => error) || (!currentStakeholder.id && (!currentStakeholder.nome && !currentStakeholder.ragione_sociale))}
                    >
                        {(currentStakeholder.id) ? "Aggiorna" : "Salva"}
                    </Button>
                    {currentStakeholder.id && (
                        <Button variant="outlined" color="error" onClick={deletestakeholder} disabled={!isAdmin}>
                            Elimina
                        </Button>
                    )}
                </Box>
            </Paper>
        </Box>
    );
};

export default Stakeholder;